import React, { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useMeterReadingCounts } from '../../hooks/useMeters';
import {
  buildCoverage,
  summariseCoverage,
  describeCoverage,
  describeReported,
  describeDay,
  hasVolume,
} from '../../utils/meterCoverage';
import '../../styles/DataCompleteness.css';

/** Days of history the strip covers. */
const WINDOW_DAYS = 30;

/**
 * How much each day of the last month moved, and which days reported at all.
 *
 * One row of days, which is what a month of a single meter reads as. A
 * calendar grid was tried and reverted: seven wide columns over five rows
 * turned thirty small facts into thirty large blocks, and the shape it was
 * meant to reveal cost more room than the shape was worth.
 *
 * What a day held is written under the strip rather than left to a `title`
 * attribute. A native tooltip never fires on touch, so on a tablet in a plant
 * those thirty squares said nothing at all; it also waits a second, cannot be
 * reached by keyboard, and cannot be styled. The line reads instantly, stays
 * in one place so the eye does not chase it, and answers on hover, tap or
 * arrow key alike.
 *
 * A consumption total covering days the meter never reported is understated,
 * and nothing else on the page says so — the cards read the same whether a
 * month is whole or has a week missing from it. This reads the count of
 * readings per day, the only signal that separates a dead gateway from a
 * closed valve. The count colours the squares and then stays out of the way:
 * pointing at one gives the day's consumption, which is what the reader is on
 * this page for.
 *
 * @param {string} meterId - The meter to report on.
 */
const DataCompleteness = ({ meterId }) => {
  const { data, isLoading, error } = useMeterReadingCounts(meterId, WINDOW_DAYS);
  const [picked, setPicked] = useState(null);

  const { days, summary, shaded } = useMemo(() => {
    const built = buildCoverage(data?.days, data?.from, data?.to);

    return {
      days: built,
      summary: summariseCoverage(built),
      shaded: hasVolume(built),
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="im-cov im-cov--quiet">
        <Spinner animation="border" size="sm" variant="primary" />
      </div>
    );
  }

  if (error || !days.length) {
    return (
      <div className="im-cov im-cov--quiet">
        Reading history could not be loaded for this meter.
      </div>
    );
  }

  /**
   * The day the readout is describing.
   *
   * The most recent one until a reader points at another, so the line is never
   * blank and never changes height — and the day they came to look at is the
   * one already written out.
   */
  const shown = days.find((day) => day.date === picked) || days[days.length - 1];

  return (
    <div className="im-cov">
      <div className="im-cov-top">
        <h3>Last {summary.total} days</h3>
        <span className={`im-cov-score${summary.whole ? '' : ' im-cov-score--warn'}`}>
          {describeReported(summary)}
        </span>
        {describeCoverage(summary) && (
          <span className="im-cov-why">{describeCoverage(summary)}</span>
        )}
      </div>

      <div className="im-cov-strip" onMouseLeave={() => setPicked(null)}>
        {days.map((day) => (
          <button
            key={day.date}
            type="button"
            className={`im-cov-day im-cov-day--${shaded ? day.band : day.state}${
              day.date === shown.date ? ' im-cov-day--on' : ''
            }`}
            aria-label={describeDay(day, data?.unit)}
            onMouseEnter={() => setPicked(day.date)}
            onFocus={() => setPicked(day.date)}
            onClick={() => setPicked(day.date)}
          />
        ))}
      </div>

      <div className="im-cov-axis">
        <span>{days[0].label}</span>
        <span>{days[days.length - 1].label}</span>
      </div>

      <p className="im-cov-readout">{describeDay(shown, data?.unit)}</p>

      <div className="im-cov-key">
        {shaded ? (
          <>
            <span><i className="im-cov-swatch im-cov-swatch--high" />Above usual</span>
            <span><i className="im-cov-swatch im-cov-swatch--usual" />Usual</span>
            <span><i className="im-cov-swatch im-cov-swatch--low" />Below usual</span>
            <span><i className="im-cov-swatch im-cov-swatch--none" />No readings</span>
          </>
        ) : (
          <>
            <span><i className="im-cov-swatch im-cov-swatch--full" />Full day</span>
            <span><i className="im-cov-swatch im-cov-swatch--partial" />Partial</span>
            <span><i className="im-cov-swatch im-cov-swatch--dark" />No readings</span>
          </>
        )}
      </div>
    </div>
  );
};

export default DataCompleteness;
